import { useState, useEffect } from "react";
import { AlertTriangle, Activity, Info } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const SeismicAlert = () => {
  const [seismicData, setSeismicData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDetails, setShowDetails] = useState(false);

  const fetchSeismicData = async () => {
    try {
      const response = await axios.get(`${API}/seismic/alerts`);
      setSeismicData(response.data);
    } catch (error) {
      console.error("Error fetching seismic data:", error);
      setSeismicData({ success: false, alert_level: "unknown", earthquakes: [] });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSeismicData();
    // Refresh every 5 minutes
    const interval = setInterval(fetchSeismicData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const getAlertStyle = (level) => {
    switch (level) {
      case "critical":
        return "bg-red-600/30 text-red-400 border-red-500/50 animate-pulse";
      case "warning":
        return "bg-orange-500/30 text-orange-400 border-orange-500/50";
      case "minor":
        return "bg-yellow-500/30 text-yellow-400 border-yellow-500/50";
      case "micro":
        return "bg-blue-500/30 text-blue-400 border-blue-500/50";
      case "none":
        return "bg-emerald-500/20 text-emerald-400 border-emerald-500/30";
      default:
        return "bg-zinc-500/20 text-zinc-400 border-zinc-500/30";
    }
  };

  const getAlertIcon = (level) => {
    if (level === "critical" || level === "warning") {
      return <AlertTriangle className="w-3 h-3 mr-1" />;
    }
    return <Activity className="w-3 h-3 mr-1" />;
  };

  const getAlertText = (level, magnitude) => {
    switch (level) {
      case "critical":
        return `SISMO ${magnitude.toFixed(1)}`;
      case "warning":
        return `SISMO ${magnitude.toFixed(1)}`;
      case "minor":
        return `Sismo ${magnitude.toFixed(1)}`;
      case "micro":
        return `Micro ${magnitude.toFixed(1)}`;
      case "none":
        return "SIN SISMOS";
      default:
        return "---";
    }
  };

  const getMagnitudeColor = (mag) => {
    if (mag >= 5.0) return "text-red-500";
    if (mag >= 4.0) return "text-orange-500";
    if (mag >= 3.0) return "text-yellow-500";
    if (mag >= 2.0) return "text-blue-400";
    return "text-zinc-400";
  };

  if (loading) {
    return (
      <Badge variant="outline" className="bg-zinc-500/20 text-zinc-400 border-zinc-500/30 text-xs py-1 px-2 h-7">
        <Activity className="w-3 h-3 mr-1 animate-pulse" />
        USGS...
      </Badge>
    );
  }

  const alertLevel = seismicData?.alert_level || "unknown";
  const maxMag = seismicData?.max_magnitude || 0;

  return (
    <>
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge 
              variant="outline" 
              className={`${getAlertStyle(alertLevel)} text-xs py-1 px-2 h-7 cursor-pointer`}
              onClick={() => setShowDetails(true)}
            >
              {getAlertIcon(alertLevel)}
              {getAlertText(alertLevel, maxMag)}
            </Badge>
          </TooltipTrigger>
          <TooltipContent className="bg-zinc-800 text-zinc-100 border-zinc-700">
            <p>Alertas sísmicas USGS ({seismicData?.radius_km || 100}km)</p>
            {seismicData?.total_nearby > 0 && (
              <p className="text-xs text-zinc-400">
                {seismicData.total_nearby} sismo(s) detectado(s)
              </p>
            )}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {/* Details Dialog */}
      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-100 flex items-center gap-2">
              <Activity className="w-5 h-5 text-orange-400" />
              Actividad Sísmica - USGS
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="bg-zinc-800/50 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-zinc-400 uppercase tracking-wider">Radio de búsqueda</span>
                <span className="text-sm font-mono text-zinc-300">{seismicData?.radius_km || 100} km</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-zinc-400 uppercase tracking-wider">Referencia</span>
                <span className="text-sm text-zinc-300">La Aldea de San Nicolás</span>
              </div>
            </div>

            {seismicData?.earthquakes?.length > 0 ? (
              <div className="space-y-2">
                <h4 className="text-xs text-zinc-400 uppercase tracking-wider">Sismos recientes</h4>
                {seismicData.earthquakes.map((eq, index) => (
                  <div 
                    key={eq.id || index} 
                    className="bg-zinc-800/30 rounded-lg p-3 border border-zinc-700/50"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className={`text-2xl font-bold ${getMagnitudeColor(eq.magnitude)}`}>
                        {eq.magnitude.toFixed(1)}
                      </span>
                      <span className="text-xs text-zinc-500">Richter</span>
                    </div>
                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between">
                        <span className="text-zinc-500">Fecha:</span>
                        <span className="text-zinc-300">{eq.date} {eq.time}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-zinc-500">Distancia:</span>
                        <span className="text-zinc-300">{eq.distance_km} km</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-zinc-500">Profundidad:</span>
                        <span className="text-zinc-300">{eq.depth_km} km</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-zinc-500">Ubicación:</span>
                        <span className="text-zinc-300 text-right max-w-[180px] truncate">{eq.location}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <Activity className="w-10 h-10 text-emerald-400 mx-auto mb-2" />
                <p className="text-zinc-400">Sin actividad sísmica significativa</p>
                <p className="text-xs text-zinc-500 mt-1">en los últimos 10 días</p>
              </div>
            )}

            <div className="flex items-center gap-2 text-[10px] text-zinc-500 pt-2 border-t border-zinc-800">
              <Info className="w-3 h-3" />
              <span>Datos: USGS Earthquake Catalog</span>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SeismicAlert;
